// mds-dojo — dijit/_TimePicker — FAITHFUL port of references/dijit/_TimePicker.js
// (BSD-3, © JS Foundation). Codemod pipeline; declare from the faithful runtime.
// ADAPTATIONS:
//  - `dojo/_base/declare` → `../dojo/_base/declare-runtime`; `dojo/date*` → `../dojo/date*`.
//  - Unused imports dropped (dojo/_base/array, dojo/sniff, dojo/query, dojo/mouse, dojo/on,
//    dojo/_base/lang were declared in the source but never referenced in the body).
//  - `kernel.deprecated` guarded (the slim mds kernel has no deprecated()).
//  - `this.emit("input")` uses _WidgetBase.emit (unchanged).
import ddate from '../dojo/date'; // date.compare
import locale from '../dojo/date/locale'; // locale.format
import stamp from '../dojo/date/stamp'; // stamp.fromISOString stamp.toISOString
import declare from '../dojo/_base/declare-runtime'; // declare
import domClass from '../dojo/dom-class'; // domClass.add domClass.contains domClass.toggle
import domConstruct from '../dojo/dom-construct'; // domConstruct.create
import kernel from '../dojo/_base/kernel'; // kernel.deprecated
import keys from '../dojo/keys'; // keys
import _WidgetBase from './_WidgetBase';
import _ListMouseMixin from './form/_ListMouseMixin';

// module:
//		dijit/_TimePicker

var TimePicker: any = declare("dijit._TimePicker", [_WidgetBase, _ListMouseMixin], {
	// summary:
	//		A time picker dropdown, used by dijit/form/TimeTextBox.
	//		This widget is not available as a standalone widget due to lack of accessibility support.

	// baseClass: [protected] String
	//		The root className to use for the various states of this widget
	baseClass: "dijitTimePicker",

	// pickerMin: String
	//		ISO-8601 string representing the time of the first
	//		visible element in the time picker.
	//		Set in local time, without a time zone.
	pickerMin: "T00:00:00",

	// pickerMax: String
	//		ISO-8601 string representing the last (possible) time
	//		added to the time picker.
	//		Set in local time, without a time zone.
	pickerMax: "T23:59:59",

	// clickableIncrement: String
	//		ISO-8601 string representing the interval between choices in the time picker.
	//		Set in local time, without a time zone.
	//		Example: `T00:15:00` creates 15 minute increments
	//		Must divide dijit/_TimePicker.visibleIncrement evenly
	clickableIncrement: "T00:15:00",

	// visibleIncrement: String
	//		ISO-8601 string representing the interval between "major" choices in the time picker.
	//		Each theme will highlight the major choices with a larger font / different color / etc.
	//		Set in local time, without a time zone.
	//		Example: `T01:00:00` creates text in every 1 hour increment
	visibleIncrement: "T01:00:00",

	// value: String
	//		Time to display.
	//		Defaults to current time.
	//		Can be a Date object or an ISO-8601 string.
	value: new Date(),

	_visibleIncrement: 2,
	_clickableIncrement: 1,
	_totalIncrements: 10,

	// constraints: TimePicker.__Constraints
	//		Specifies valid range of times (start time, end time), and also used by TimeTextBox to pass other
	//		options to the TimePicker: pickerMin, pickerMax, clickableIncrement, and visibleIncrement.
	constraints: {},

	serialize: stamp.toISOString,

	buildRendering: function () {
		this.inherited(arguments);
		this.containerNode = this.domNode;	// expected by _ListBase
		this.timeMenu = this.domNode;	// for back-compat
	},

	setValue: function (/*Date*/ value: any) {
		// summary:
		//		Deprecated.  Used set('value') instead.
		// tags:
		//		deprecated
		(kernel as any).deprecated && (kernel as any).deprecated("dijit._TimePicker:setValue() is deprecated.  Use set('value', ...) instead.", "", "2.0");
		this.set('value', value);
	},

	_setValueAttr: function (/*Date*/ date: any) {
		// summary:
		//		Hook so set('value', ...) works.
		this._set("value", date);
		this._showText();
	},

	_setFilterStringAttr: function (val: any) {
		// summary:
		//		Called by TimeTextBox to filter the values shown in my list
		this._set("filterString", val);
		this._showText();
	},

	isDisabledDate: function (/*===== dateObject, locale =====*/) {
		// summary:
		//		May be overridden to disable certain dates in the TimePicker e.g. `isDisabledDate=locale.isWeekend`
		return false; // Boolean
	},

	_getFilteredNodes: function (/*number*/ start?: any, /*number*/ maxNum?: any, /*Boolean*/ before?: any, /*DOMNode*/ lastNode?: any) {
		// summary:
		//		Returns a DocumentFragment of nodes with the filter applied.

		var nodes = this.ownerDocument.createDocumentFragment();

		for (var i = 0; i < this._maxIncrement; i++) {
			var n = this._createOption(i);
			if (n) {
				nodes.appendChild(n);
			}
		}

		return nodes;
	},

	_showText: function () {
		// summary:
		//		Displays the relevant choices in the drop down list
		var fromIso = stamp.fromISOString;
		this.domNode.innerHTML = "";
		this._clickableIncrementDate = fromIso(this.clickableIncrement);
		this._visibleIncrementDate = fromIso(this.visibleIncrement);
		// get the value of the increments to find out how many divs to create
		var
			sinceMidnight = function (/*Date*/ date: any) {
				return date.getHours() * 60 * 60 + date.getMinutes() * 60 + date.getSeconds();
			},
			clickableIncrementSeconds = sinceMidnight(this._clickableIncrementDate),
			visibleIncrementSeconds = sinceMidnight(this._visibleIncrementDate),
			// round reference date to previous visible increment
			time = (this.value || this.currentFocus).getTime();

		this._refDate = fromIso(this.pickerMin);
		this._refDate.setFullYear(1970, 0, 1); // match parse defaults

		// assume clickable increment is the smallest unit
		this._clickableIncrement = 1;
		// divide the visible increments by the clickable increments to get how often to display the time inline
		this._visibleIncrement = visibleIncrementSeconds / clickableIncrementSeconds;

		// get the number of increments (i.e. number of entries in the picker)
		var endDate = fromIso(this.pickerMax);
		endDate.setFullYear(1970, 0, 1);
		var visibleRange = (endDate.getTime() - this._refDate.getTime()) * 0.001;
		this._maxIncrement = Math.ceil((visibleRange + 1) / clickableIncrementSeconds);

		var nodes = this._getFilteredNodes();

		// never show empty due to a bad filter
		if (!nodes.firstChild && this.filterString) {
			this.filterString = '';
			this._showText();
		} else {
			this.domNode.appendChild(nodes);
		}
	},

	constructor: function (/*===== params, srcNodeRef =====*/) {
		// summary:
		//		Create the widget.
		this.constraints = {};
	},

	postMixInProperties: function () {
		this.inherited(arguments);
		this._setConstraintsAttr(this.constraints); // this needs to happen now (and later) due to codependency on _set*Attr calls
	},

	// For historical reasons TimeTextBox sends all the options for the _TimePicker inside of a constraints{} object
	_setConstraintsAttr: function (/* Object */ constraints: any) {
		// brings in increments, etc.
		for (var key in { clickableIncrement: 1, visibleIncrement: 1, pickerMin: 1, pickerMax: 1 }) {
			if (key in constraints) {
				this[key] = constraints[key];
			}
		}

		// locale needs the lang in the constraints as locale
		if (!constraints.locale) {
			constraints.locale = this.lang;
		}
	},

	_createOption: function (/*Number*/ index: any) {
		// summary:
		//		Creates a clickable time option, or returns null if the specified index doesn't match the filter
		var date = new Date(this._refDate);
		var incrementDate = this._clickableIncrementDate;
		date.setHours(date.getHours() + incrementDate.getHours() * index,
			date.getMinutes() + incrementDate.getMinutes() * index,
			date.getSeconds() + incrementDate.getSeconds() * index);
		if (this.constraints.selector == "time") {
			date.setFullYear(1970, 0, 1); // make sure each time is for the same date
		}
		var dateString = locale.format(date, this.constraints);
		if (this.filterString && dateString.toLowerCase().indexOf(this.filterString) !== 0) {
			// Doesn't match the filter - return null
			return null;
		}

		var div = this.ownerDocument.createElement("div");
		div.className = this.baseClass + "Item";
		div.date = date;
		div.idx = index;
		domConstruct.create('div', {
			"class": this.baseClass + "ItemInner",
			innerHTML: dateString
		}, div);

		var marker = index % this._visibleIncrement < 1 && index % this._visibleIncrement > -1,
			tick = !marker && !(index % this._clickableIncrement);
		if (marker) {
			div.className += " " + this.baseClass + "Marker";
		} else if (tick) {
			div.className += " " + this.baseClass + "Tick";
		}

		if (this.isDisabledDate(date)) {
			// set disabled
			div.className += " " + this.baseClass + "ItemDisabled";
		}
		if (this.value && !ddate.compare(this.value, date, this.constraints.selector)) {
			div.selected = true;
			div.className += " " + this.baseClass + "ItemSelected";
			this._selectedDiv = div;
			if (marker) {
				div.className += " " + this.baseClass + "MarkerSelected";
			} else if (tick) {
				div.className += " " + this.baseClass + "TickSelected";
			}

			// Initially highlight the current value.
			this._highlightOption(div, true);
		}
		return div;
	},

	onOpen: function () {
		this.inherited(arguments);

		// Since _ListBase::_setSelectedAttr() calls scrollIntoView(), shouldn't call it until list is visible.
		this.set("selected", this._selectedDiv);
	},

	_onOptionSelected: function (/*Object*/ tgt: any, /*Boolean*/ change: any) {
		// summary:
		//		Called when user clicks or keys to an option in the drop down list
		var tdate = tgt.target.date || tgt.target.parentNode.date;
		if (!tdate || this.isDisabledDate(tdate)) {
			return;
		}
		this._set('value', tdate);
		this.emit("input");
		if (change) {
			this._highlighted_option = null;
			this.set('value', tdate);
			this.onChange(tdate);
		}
	},

	onChange: function (/*Date*/ /*===== time =====*/) {
		// summary:
		//		Notification that a time was selected.  It may be the same as the previous value.
	},

	_highlightOption: function (/*node*/ node: any, /*Boolean*/ highlight: any) {
		// summary:
		//		Turns on/off highlight effect on a node based on mouse out/over event
		if (!node) {
			return;
		}
		if (highlight) {
			if (this._highlighted_option) {
				this._highlightOption(this._highlighted_option, false);
			}
			this._highlighted_option = node;
		} else if (this._highlighted_option !== node) {
			return;
		} else {
			this._highlighted_option = null;
		}
		domClass.toggle(node, this.baseClass + "ItemHover", highlight);
		if (domClass.contains(node, this.baseClass + "Marker")) {
			domClass.toggle(node, this.baseClass + "MarkerHover", highlight);
		} else {
			domClass.toggle(node, this.baseClass + "TickHover", highlight);
		}
	},

	handleKey: function (/*Event*/ e: any) {
		// summary:
		//		Called from `dijit/form/_DateTimeTextBox` to pass a keypress event
		//		from the `dijit/form/TimeTextBox` to be handled in this widget
		if (e.keyCode == keys.DOWN_ARROW) {
			this.selectNextNode();
			this._onOptionSelected({ target: this._highlighted_option }, false);
			e.stopPropagation();
			e.preventDefault();
			return false;
		} else if (e.keyCode == keys.UP_ARROW) {
			this.selectPreviousNode();
			this._onOptionSelected({ target: this._highlighted_option }, false);
			e.stopPropagation();
			e.preventDefault();
			return false;
		} else if (e.keyCode == keys.ENTER || e.keyCode === keys.TAB) {
			// mouse hover followed by TAB is NO selection
			if (!this._keyboardSelected && e.keyCode === keys.TAB) {
				return true;	// true means don't call stopEvent()
			}

			// Accept the currently-highlighted option as the value
			if (this._highlighted_option) {
				this._onOptionSelected({ target: this._highlighted_option }, true);
			}

			// Call stopEvent() for ENTER key so that form doesn't submit,
			// but not for TAB, so that TAB does switch focus
			return e.keyCode === keys.TAB;
		}
		return undefined;
	},

	// Implement abstract methods for _ListBase
	onHover: function (/*DomNode*/ node: any) {
		this._highlightOption(node, true);
	},

	onUnhover: function (/*DomNode*/ node: any) {
		this._highlightOption(node, false);
	},

	onSelect: function (/*DomNode*/ node: any) {
		this._highlightOption(node, true);
	},

	onDeselect: function (/*DomNode*/ node: any) {
		this._highlightOption(node, false);
	},

	onClick: function (/*DomNode*/ node: any) {
		this._onOptionSelected({ target: node }, true);
	}
});

export default TimePicker;
